import type { Vehicle } from './Vehicle';
import { z } from 'zod';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { VehiclesApi } from './VehiclesApi';
import { api } from '../../../stores/api';

export const VehicleExtremum = {
    weightLimit: {
        min: 0,
        max: 40000,
        unit: 'kg',
    },
    parcelLimit: {
        min: 0,
        max: 10000000,
        unit: '',
    },
    truckTotalWeight: {
        min: 0,
        max: 40,
        unit: 't',
    },
    truckAxisLoad: {
        min: 0,
        max: 11.5,
        unit: 't',
    },
    truckLength: {
        min: 0,
        max: 18,
        unit: 'm',
    },
    truckWidth: {
        min: 0,
        max: 2.8,
        unit: 'm',
    },
    truckHeight: {
        min: 0,
        max: 4.1,
        unit: 'm',
    },
};

export class VehicleValidator {
    public static validateAll(vehicle: Vehicle): boolean {
        return (
            ((VehicleValidator.isEmailValid(vehicle) === '' || vehicle.get()?.is_group) ?? false) &&
            VehicleValidator.isWeightLimitValid(vehicle) === '' &&
            VehicleValidator.isParcelLimitValid(vehicle) === '' &&
            VehicleValidator.isTruckParametersValid(vehicle) &&
            VehicleValidator.isWarehouseIdValid(vehicle)
        );
    }

    public static isEmailValid(vehicle: Vehicle): string {
        try {
            z.string().email().parse(vehicle.getEmail());
        } catch (e) {
            console.warn('VehicleValidator.isEmailValid', e?.message);
            return 'E-mail nie jest poprawny';
        }
        return '';
    }

    public static isEmailAvailable(email: string, verifyEmailTimeout: any): string {
        let result = false;
        let error: string = '';

        function verifyEmail(email: string) {
            VehiclesApi.verifyEmail(get(api), email)
                .then(result => {
                    error = result.valid ? '' : get(_)('vehicle-email-error-' + result.reason);
                })
                .catch(err => {
                    error = err;
                })
                .finally(() => {});
        }

        if (email) {
            if (verifyEmailTimeout) {
                clearTimeout(verifyEmailTimeout);
                verifyEmailTimeout = null;
            }
            if (email === '') {
                error = 'empty-email';
                return error;
            }

            if (z.string().email().safeParse(email).success) {
                verifyEmailTimeout = setTimeout(function () {
                    error = '';
                    verifyEmail(email);
                }, 500);
            } else {
                verifyEmailTimeout = setTimeout(function () {
                    error = 'invalid-email';
                }, 500);
            }
        }

        return error;
    }

    public static isWeightLimitValid(vehicle: Vehicle): string {
        try {
            z.coerce
                .number()
                .min(VehicleExtremum.weightLimit.min)
                .max(VehicleExtremum.weightLimit.max)
                .nullable()
                .parse(vehicle.getWeightLimit());
        } catch (e) {
            console.warn('VehicleValidator.isWeightLimitValid', e);
            return get(_)('Vehicle_weight_limit_error', {
                values: {
                    min: VehicleExtremum.weightLimit.min,
                    max: VehicleExtremum.weightLimit.max,
                },
            });
        }
        return '';
    }

    public static isParcelLimitValid(vehicle: Vehicle): string {
        try {
            z.coerce
                .number()
                .min(VehicleExtremum.parcelLimit.min)
                .max(VehicleExtremum.parcelLimit.max)
                .nullable()
                .parse(vehicle.getParcelLimit());
        } catch (e) {
            console.warn('VehicleValidator.isParcelLimitValid', e);
            return get(_)('Vehicle_parcel_limit_error', {
                values: {
                    min: VehicleExtremum.parcelLimit.min,
                    max: VehicleExtremum.parcelLimit.max,
                },
            });
        }
        return '';
    }

    public static isTruckTotalWeightValid(vehicle: Vehicle): boolean {
        return z.coerce
            .number()
            .min(VehicleExtremum.truckTotalWeight.min)
            .max(VehicleExtremum.truckTotalWeight.max)
            .nullable()
            .safeParse(vehicle.getTruckTotalWeight()).success;
    }

    public static isTruckAxisLoadValid(vehicle: Vehicle): boolean {
        return z.coerce
            .number()
            .min(VehicleExtremum.truckAxisLoad.min)
            .max(VehicleExtremum.truckAxisLoad.max)
            .nullable()
            .safeParse(vehicle.getTruckAxisLoad()).success;
    }

    public static isTruckLengthValid(vehicle: Vehicle): boolean {
        return z.coerce
            .number()
            .min(VehicleExtremum.truckLength.min)
            .max(VehicleExtremum.truckLength.max)
            .nullable()
            .safeParse(vehicle.getTruckLength()).success;
    }

    public static isTruckWidthValid(vehicle: Vehicle): boolean {
        return z.coerce
            .number()
            .min(VehicleExtremum.truckWidth.min)
            .max(VehicleExtremum.truckWidth.max)
            .nullable()
            .safeParse(vehicle.getTruckWidth()).success;
    }

    public static isTruckHeightValid(vehicle: Vehicle): boolean {
        return z.coerce
            .number()
            .min(VehicleExtremum.truckHeight.min)
            .max(VehicleExtremum.truckHeight.max)
            .nullable()
            .safeParse(vehicle.getTruckHeight()).success;
    }

    public static isTruckParametersValid(vehicle: Vehicle): boolean {
        return (
            VehicleValidator.isTruckTotalWeightValid(vehicle) &&
            VehicleValidator.isTruckAxisLoadValid(vehicle) &&
            VehicleValidator.isTruckLengthValid(vehicle) &&
            VehicleValidator.isTruckWidthValid(vehicle) &&
            VehicleValidator.isTruckHeightValid(vehicle)
        );
    }

    public static isWarehouseIdValid(vehicle: Vehicle): boolean {
        return vehicle.getWarehouseId() !== null;
    }

    public static isRouteCostParamValid(vehicle: Vehicle, param: string): boolean {
        return z.coerce
            .number()
            .min(0)
            .nullable()
            .safeParse(vehicle.getRouteCost(param)).success;
    }
}
